import { FC, useState } from "react";
import { FormikHelpers } from "formik";
import {
  useAutocomplete,
  Form,
  Button,
  Modal,
  MessageField,
  FormButtons,
  FormField,
  useFileParser,
  FormField as UiKitFormField,
} from "@epcnetwork/core-ui-kit";

import { filterDatabasesPipelines } from "utils";
import { suppressionCheckboxLabel } from "constants/pipelines.constants";
import { FormField as LocalFormField } from "components";
import { getDatabasesNames } from "api";
import { MatchFileItem, MatchValues } from "../match.types";
import { validationSchema } from "../match.constants";

import styles from "../match.module.css";

type MatchModalFormProps = {
  file: MatchFileItem;
  setClose: VoidFunction;
  onSubmitClick: (fileValues: MatchFileItem) => void;
};

export const MatchFormModal: FC<MatchModalFormProps> = ({ file, setClose, onSubmitClick }) => {
  const { loading, parsedFile, error } = useFileParser(file.originalFile);

  const [initialValues] = useState<MatchValues>({
    fileValues: {
      emailIndex: file.data.emailIndex,
      suppress: file.data.suppress,
      headers: file.data.headers,
      databases: file.data.databases,
    },
  });

  const databasesAutocomplete = useAutocomplete(getDatabasesNames, "name", "name");
  databasesAutocomplete.fetchOptions = filterDatabasesPipelines(databasesAutocomplete.fetchOptions);

  const onSubmit = (values: MatchValues, { setSubmitting }: FormikHelpers<MatchValues>) => {
    const isDialmap = values.fileValues.databases.includes("dialmap");
    const fileValues = {
      ...values.fileValues,
      suppress: isDialmap ? false : values.fileValues.suppress || false,
    };

    onSubmitClick({
      ...file,
      isTuned: true,
      data: {
        ...file.data,
        ...fileValues,
      },
    });
    setSubmitting(false);
  };

  return (
    <Modal isOpen={Boolean(file)} setClose={setClose} contentClassName={styles.modal}>
      <h2>Set values {file.originalFile.name}</h2>
      <MessageField message={error} />
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        loading={loading}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => {
          const emailColumnSelected = values.fileValues.emailIndex !== -1;

          const isDialmap = values.fileValues.databases.includes("dialmap");

          const getOptions = () => {
            if (values.fileValues.databases.length === 0) return databasesAutocomplete.fetchOptions;

            return databasesAutocomplete.fetchOptions.map((option) =>
              option.value === "dialmap"
                ? { ...option, locked: !isDialmap }
                : { ...option, locked: isDialmap },
            );
          };

          const onSelectAll = async () => {
            /* select all databases except dialmap */
            const allDatabases = databasesAutocomplete.fetchOptions
              .map((option) => option.value)
              .filter((value) => value !== "dialmap");

            await setFieldValue("fileValues.databases", allDatabases);
          };

          const autocomplete = { ...databasesAutocomplete, fetchOptions: getOptions() };

          return (
            <>
              <FormField
                type="select"
                name="fileValues.databases"
                label="Databases"
                placeholder="Databases"
                asyncOptions={autocomplete}
                isMulti
                canSelectAll
                onSelectAll={onSelectAll}
              />
              <LocalFormField
                type="file-table"
                name="fileValues"
                label={isDialmap ? "Select Phone column" : "Select Email column"}
                parsedFile={parsedFile}
                loading={loading}
                selector="radio"
                autocompleteColumns={false}
              />

              <div className={styles.actions}>
                {isDialmap ? (
                  <div />
                ) : (
                  <UiKitFormField
                    type="checkbox"
                    name="fileValues.suppress"
                    label={suppressionCheckboxLabel}
                    disabled={!emailColumnSelected}
                    disableError
                  />
                )}

                <FormButtons>
                  <Button appearance="secondary" onClick={setClose}>
                    Cancel
                  </Button>
                  <Button type="submit">Set values</Button>
                </FormButtons>
              </div>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};
